import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  useSurveyQueryActiveBrandGroup,
  useSurveyQueryData,
  useSurveyQuerySurveyFilterTextGroups,
} from '~/hooks/useSurveyQuery'
import {
  assertExists,
  dataDisplayTypeLabelI18nKeyMap,
  type FigureInfo,
  FigureInfoHelpers,
  segmentDisplayTypeLabelI18nKeyMap,
} from '~/utils'

import {
  dataDisplayTypeAtom,
  segmentDisplayTypeAtom,
  tabulatedBrandIdsAtom,
  tabulatedColumnCustomerAttributeIdsAtom,
  tabulatedCustomerAttributeIdsAtom,
  tabulatedQuestionIdsAtom,
} from '../atoms'
import { pathKey } from '../utils'

export const useFigureInfo = (): FigureInfo => {
  const { t } = useTranslation()
  const survey = useSurveyQueryData()
  const brandGroup = useSurveyQueryActiveBrandGroup()

  const tabulatedColumnCustomerAttributeIds = useAtomValue(
    tabulatedColumnCustomerAttributeIdsAtom,
  )
  const tabulatedBrandIds = useAtomValue(tabulatedBrandIdsAtom)
  const tabulatedQuestionIds = useAtomValue(tabulatedQuestionIdsAtom)
  const tabulatedCustomerAttributeIds = useAtomValue(
    tabulatedCustomerAttributeIdsAtom,
  )
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)

  const surveyFilterTextGroups = useSurveyQuerySurveyFilterTextGroups()

  return React.useMemo(() => {
    assertExists(survey, t('assertion_failed'))
    const brandAccountId = survey.brandAccountId

    return {
      brandAccountId,
      brandGroup: brandGroup?.name,
      dataDisplayType: dataDisplayTypeLabelI18nKeyMap[dataDisplayType],
      pathKey,
      columnCustomerAttributes: FigureInfoHelpers.toCustomerAttributeTexts(
        tabulatedColumnCustomerAttributeIds,
        survey.customerAttributeGroups,
        t,
      ),
      brand: brandGroup?.brands
        .filter((brand) => tabulatedBrandIds.includes(brand.id))
        .map((brand) => brand.name)
        .join(', '),
      questions: FigureInfoHelpers.toQuestionTexts(
        tabulatedQuestionIds,
        survey.questions,
      ),
      customerAttributes: FigureInfoHelpers.toCustomerAttributeTexts(
        tabulatedCustomerAttributeIds,
        survey.customerAttributeGroups,
        t,
      ),
      segmentDisplayType: segmentDisplayTypeLabelI18nKeyMap[segmentDisplayType],
      survey: survey.title,
      surveyFilter: surveyFilterTextGroups,
    }
  }, [
    brandGroup?.brands,
    brandGroup?.name,
    tabulatedBrandIds,
    tabulatedColumnCustomerAttributeIds,
    tabulatedCustomerAttributeIds,
    dataDisplayType,
    tabulatedQuestionIds,
    segmentDisplayType,
    survey,
    surveyFilterTextGroups,
    t,
  ])
}
